import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type Corner = 'tl' | 'tr' | 'bl' | 'br';

const MARGIN = 16;

function getCornerPos(corner: Corner, el: { offsetWidth: number; offsetHeight: number }) {
  const w = el.offsetWidth || 36;
  const h = el.offsetHeight || 36;
  switch (corner) {
    case 'tl':
      return { x: MARGIN, y: MARGIN };
    case 'tr':
      return { x: window.innerWidth - w - MARGIN, y: MARGIN };
    case 'bl':
      return { x: MARGIN, y: window.innerHeight - h - MARGIN };
    case 'br':
      return { x: window.innerWidth - w - MARGIN, y: window.innerHeight - h - MARGIN };
  }
}

type UseDraggableOptions = {
  onTap: () => void;
};

export function useDraggable({ onTap }: UseDraggableOptions) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number } | null>(null);
  const didDrag = useRef(false);
  const cornerRef = useRef<Corner>('br');
  const [corner, setCorner] = useState<Corner>('br');

  // Initial position
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }
    const pos = getCornerPos(corner, el);
    el.style.left = `${pos.x}px`;
    el.style.top = `${pos.y}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reposition on window resize
  useEffect(() => {
    const onResize = () => {
      const el = containerRef.current;
      if (!el) {
        return;
      }
      const pos = getCornerPos(cornerRef.current, el);
      el.style.transition = 'none';
      el.style.left = `${pos.x}px`;
      el.style.top = `${pos.y}px`;
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('[data-bell-trigger]')) {
      dragState.current = { startX: e.clientX, startY: e.clientY };
      didDrag.current = false;
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    }
  }, []);

  const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    if (!dragState.current || !el) {
      return;
    }

    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;

    if (!didDrag.current && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
      didDrag.current = true;
      el.style.transition = 'none';
      el.style.cursor = 'grabbing';
    }

    if (didDrag.current) {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const x = Math.max(0, Math.min(window.innerWidth - w, e.clientX - w / 2));
      const y = Math.max(0, Math.min(window.innerHeight - h, e.clientY - h / 2));
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
    }
  }, []);

  const handlePointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const wasDrag = didDrag.current;
      const start = dragState.current;
      const el = containerRef.current;
      dragState.current = null;

      const cachedWidth = el?.offsetWidth ?? 36;
      const cachedHeight = el?.offsetHeight ?? 36;

      if (el) {
        el.style.cursor = '';
      }

      if (wasDrag && start && el) {
        const dx = e.clientX - start.startX;
        const dy = e.clientY - start.startY;

        const prev = cornerRef.current;
        const isLeft = Math.abs(dx) > Math.abs(dy) ? dx < 0 : prev === 'tl' || prev === 'bl';
        const isTop = Math.abs(dy) > Math.abs(dx) ? dy < 0 : prev === 'tl' || prev === 'tr';
        const next: Corner = isTop ? (isLeft ? 'tl' : 'tr') : isLeft ? 'bl' : 'br';

        cornerRef.current = next;
        setCorner(next);

        const pos = getCornerPos(next, {
          offsetWidth: cachedWidth,
          offsetHeight: cachedHeight,
        } as HTMLElement);
        el.style.transition =
          'left 600ms cubic-bezier(0.34,1.56,0.64,1), top 600ms cubic-bezier(0.34,1.56,0.64,1)';
        el.style.left = `${pos.x}px`;
        el.style.top = `${pos.y}px`;
      } else if (start) {
        onTap();
      }

      requestAnimationFrame(() => {
        didDrag.current = false;
      });
    },
    [onTap],
  );

  // Block native click — all open/close is handled by pointer events
  useEffect(() => {
    const el = containerRef.current;
    if (!el) {
      return;
    }

    const blockClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement).closest('[data-bell-trigger]')) {
        e.stopPropagation();
        e.preventDefault();
      }
    };

    el.addEventListener('click', blockClick, true);
    return () => el.removeEventListener('click', blockClick, true);
  }, []);

  const dropdownAlign = corner === 'tr' || corner === 'br' ? ('end' as const) : ('start' as const);
  const dropdownSide = corner === 'tl' || corner === 'tr' ? ('bottom' as const) : ('top' as const);

  return {
    containerRef,
    corner,
    dropdownAlign,
    dropdownSide,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}
